import { Badge, Group, Stack, Text } from '@mantine/core';
import { ContentCard } from '../../../components/common/ContentCard';
import { Calendar } from 'react-feather';
import { composeSessionLabel } from '../sessionLabel';

// MED-1059 — match the main-column cards' icon + title header (AC1).
const TITLE_COLOR = '#1A1A2E';
import type { PostSessionSidebarNextSession } from '../../../services/api/participantPostSession';
import { getZoneCode } from '../../../utils/formatters';

interface Props {
  next: PostSessionSidebarNextSession | null;
}

const formatScheduledStart = (iso: string | null): string => {
  if (!iso) return 'TBD';
  const date = new Date(iso);
  const dateStr = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  // MED-1389: name the zone, as the coach view does.
  return `${dateStr} · ${timeStr} ${getZoneCode(null, date)}`;
};

/**
 * Next Session sidebar card (MED-806 AC16).
 *
 * Displays the upcoming session in the participant's group: title, date/time,
 * group name, plus the PARTICIPANT role badge. Renders an empty state if no
 * upcoming session exists.
 */
export function NextSessionSidebarCard({ next }: Props) {
  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      data-testid="sidebar-next-session"
      header={
        <Group justify="space-between">
          <Group gap={8} align="center" wrap="nowrap">
            <Calendar
              size={18}
              color={TITLE_COLOR}
              aria-hidden="true"
              style={{ marginTop: -4 }}
            />
            <Text fz={15} fw={700} c={TITLE_COLOR}>
              Next Session
            </Text>
          </Group>
          <Badge size="sm" variant="light" color="blue" tt="none">
            {next?.participant_role_label === 'PARTICIPANT'
              ? 'Participant'
              : ''}
          </Badge>
        </Group>
      }
    >
      {next ? (
        <Stack gap={4}>
          <Text fw={600} data-testid="sidebar-next-title">
            {/* MED-1146 (AC-C2/AC-L5) — compose the display-aware topic (else
                group name); never prepend "Session N:" onto the raw title. */}
            {composeSessionLabel({
              session_number: next.session_number,
              session_topic: next.session_topic,
              group_name: next.group_name,
              title: next.title,
            })}
          </Text>
          <Text size="sm" c="dimmed" data-testid="sidebar-next-when">
            {formatScheduledStart(next.scheduled_start)}
            {next.group_name ? ` · ${next.group_name}` : ''}
          </Text>
        </Stack>
      ) : (
        <Text size="sm" c="dimmed" data-testid="sidebar-next-empty">
          No upcoming sessions scheduled.
        </Text>
      )}
    </ContentCard>
  );
}
